import { markDropdownOutsideInteraction } from '../Dropdown/dropdownOutsideGuard'
import { ChevronDropdownIcon, ChevronDropdownUpIcon } from '../../svg'
import type { DropdownMenuContentProps } from '@radix-ui/react-dropdown-menu'
import * as React from 'react'
import { isValidElement, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from '../Button'
import { Dropdown, DropdownMenuItem } from '../Dropdown'

type PointerDownOutsideEvent = Parameters<
    NonNullable<DropdownMenuContentProps['onPointerDownOutside']>
>[0]

interface TableActionDropdownProps {
    tableActions: React.ReactNode
    className?: string
}

export const TableActionDropdown = ({
    tableActions,
    className,
}: TableActionDropdownProps) => {
    const [isClicked, setIsClicked] = useState(false)
    const { t } = useTranslation(['common', 'shared'])
    const actions = useMemo(() => {
        return React.Children.toArray(tableActions).filter(
            (action) => action !== null
        )
    }, [tableActions])

    // Close dropdown when a NEW modal overlay appears (e.g. export modal).
    // Snapshot the dialogs already on screen so this doesn't misfire when the
    // dropdown itself lives inside a modal.
    useEffect(() => {
        if (!isClicked) return

        const openDialogSelector = '[role="dialog"][data-state="open"]'
        const initialCount =
            document.querySelectorAll(openDialogSelector).length

        const observer = new MutationObserver(() => {
            const currentCount =
                document.querySelectorAll(openDialogSelector).length
            if (currentCount > initialCount) setIsClicked(false)
        })

        observer.observe(document.body, { childList: true, subtree: true })
        return () => observer.disconnect()
    }, [isClicked])

    return (
        <Dropdown
            // Keeps the menu's own border and shadow: without them the white
            // panel has no edge over a white table.
            classNames="actionDropdown z-[var(--z-dropdown)] overflow-hidden py-1 !bg-none !hover:bg-none"
            open={isClicked}
            onOpenChange={setIsClicked}
            onPointerDownOutside={(event: PointerDownOutsideEvent) => {
                markDropdownOutsideInteraction()
                setIsClicked(false)

                const originalEvent = event.detail?.originalEvent
                if (originalEvent) {
                    originalEvent.stopPropagation()
                }
            }}
            trigger={
                <Button
                    label={t('button-actions.options')}
                    iconAfter={
                        isClicked ? (
                            <ChevronDropdownUpIcon />
                        ) : (
                            <ChevronDropdownIcon />
                        )
                    }
                    color="group"
                    shape="group"
                    variant="group"
                    size="sm"
                    className={className ?? 'rounded-r-lg md:hidden'}
                />
            }
        >
            {actions.map((action, index) => {
                if (!isValidElement(action)) {
                    return null
                }

                const key = action.key ?? index

                return (
                    <DropdownMenuItem
                        itemContainerClassNames="group-item"
                        key={key}
                        onSelect={(e) => e.preventDefault()}
                    >
                        <div className="flex w-full flex-col font-sans text-xs font-bold leading-tight text-slate-400 [&_button]:flex [&_button]:w-full [&_button]:items-center [&_button]:justify-start [&_button]:gap-2 [&_button]:rounded-none [&_button]:border-none [&_button]:bg-transparent [&_button]:px-3 [&_button]:py-2 [&_button]:text-left [&_button]:text-xs [&_button]:font-bold [&_button]:text-slate-400 [&_button]:shadow-none hover:[&_button]:bg-background-hover-light hover:[&_button]:text-color-title">
                            {action}
                        </div>
                    </DropdownMenuItem>
                )
            })}
        </Dropdown>
    )
}
